import { Provider, ServiceProviderRow } from '@/types/supabase';
import { getPlatformProvider, getPlatformProviders } from '@/services/platformApi/publicCatalogApi';
import { getProviderCategoryKeys, normalizeProviderCategory } from '@/utils/providerCategories';

export interface ProviderFilters {
  categories?: string[];
  suburbs?: string[];
  verifiedOnly?: boolean;
}

export function isUuidLike(identifier: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
}

export function slugifyProviderName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

async function resolveServiceProviderId(identifier: string): Promise<string | null> {
  if (isUuidLike(identifier)) {
    return identifier;
  }

  const data = await getPlatformProviders();

  const match = (data ?? []).find((provider) => {
    if (!provider.name) {
      return false;
    }

    return slugifyProviderName(provider.name) === identifier;
  });

  return match?.id ?? null;
}

const mapServiceProviderRowToProvider = (
  item: Partial<ServiceProviderRow> & {
    id: string;
    name: string | null;
    description: string | null;
    category: string | null;
    categories: string[] | null;
    suburbs: string[] | null;
    status: string | null;
    user_id: string | null;
    image_url: string | null;
    website_url: string | null;
    whatsapp_number?: string | null;
    is_verified?: boolean | null;
    is_featured?: boolean | null;
    is_top_rated?: boolean | null;
    created_at?: string | null;
    updated_at?: string | null;
  },
  options?: {
    whatsappVerified?: boolean;
    userProfileVerified?: boolean;
  },
): Provider => ({
  id: item.id,
  name: item.name ?? '',
  image_url: item.image_url ?? '',
  description: item.description ?? '',
  category: item.category ?? '',
  categories: item.categories ?? [],
  whatsapp_number: item.whatsapp_number ?? '',
  suburbs: item.suburbs ?? [],
  is_verified: item.is_verified ?? false,
  is_featured: item.is_featured ?? false,
  rating: 0,
  review_count: 0,
  status: item.status ?? 'approved',
  user_id: item.user_id ?? '',
  created_at: item.created_at ?? new Date().toISOString(),
  website_url: item.website_url ?? null,
  is_top_rated: item.is_top_rated ?? false,
  updated_at: item.updated_at ?? new Date().toISOString(),
  whatsapp_verified: options?.whatsappVerified ?? false,
});

export async function fetchServiceProvider(id: string): Promise<ServiceProviderRow | null> {
  return getPlatformProvider(id, { includeUnapproved: true }) as Promise<ServiceProviderRow | null>;
}

export async function fetchServiceProvidersByIds(ids: string[], statusFilter = 'approved'): Promise<ServiceProviderRow[]> {
  if (statusFilter === 'approved') {
    const providers = await getPlatformProviders();
    const requestedIds = new Set(ids);

    return providers.filter((provider) => requestedIds.has(provider.id));
  }

  const providers = await Promise.all(ids.map(async (id) => {
    try {
      return await getPlatformProvider(id, { includeUnapproved: statusFilter !== 'approved' });
    } catch (error) {
      if (error instanceof Error && /404/.test(error.message)) {
        return null;
      }

      throw error;
    }
  }));
  return providers.filter((provider): provider is ServiceProviderRow => Boolean(provider) && (statusFilter === 'all' || provider.status === statusFilter));
}

export async function fetchServiceProviderForUser(id: string, isAdmin = false): Promise<Provider | null> {
  const resolvedId = await resolveServiceProviderId(id);
  if (!resolvedId) return null;
  const provider = await getPlatformProvider(resolvedId, { includeUnapproved: isAdmin });
  return provider ? mapServiceProviderRowToProvider(provider as ServiceProviderRow, {
    whatsappVerified: provider.whatsapp_verified || false,
  }) : null;
}

export async function getAllApprovedServiceProviders(): Promise<Provider[]> {
  const data = await getPlatformProviders();
  return data?.map((item) => mapServiceProviderRowToProvider(item, {
    whatsappVerified: item.whatsapp_verified || false,
  })) || [];
}

export async function checkServiceProvidersExist(): Promise<{ id: string } | null> {
  const providers = await getPlatformProviders();
  return providers[0] ? { id: providers[0].id } : null;
}

export async function fetchFilteredServiceProviders(filters: ProviderFilters): Promise<ServiceProviderRow[]> {
  const providers = await getPlatformProviders();
  return providers.filter((provider) => {
    const providerCategoryKeys = getProviderCategoryKeys(provider as never);
    const matchesCategory = !filters.categories?.length
      || filters.categories.some((category) => {
        const normalizedCategory = normalizeProviderCategory(category);
        return normalizedCategory ? providerCategoryKeys.includes(normalizedCategory) : false;
      });
    const matchesSuburb = !filters.suburbs?.length
      || filters.suburbs.some((suburb) => provider.suburbs?.includes(suburb));
    const matchesVerified = !filters.verifiedOnly || provider.is_verified === true;
    return matchesCategory && matchesSuburb && matchesVerified;
  }) as ServiceProviderRow[];
}

export async function fetchAllCategories(): Promise<string[]> {
  const data = await getPlatformProviders();
  const allCategoriesArray = (data || []).flatMap((provider: { categories?: string[] | null }) => provider.categories || []);
  return [...new Set(allCategoriesArray)];
}

export async function fetchAllSuburbs(): Promise<string[]> {
  const data = await getPlatformProviders();
  const allSuburbsArray = (data || []).flatMap((provider: { suburbs?: string[] | null }) => provider.suburbs || []);
  return [...new Set(allSuburbsArray)];
}